import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'

import { useVendorStore } from '@/stores'
import liffapi from '@/liffapi'

export const useWorkerStore = defineStore('worker', {
  state: () => ({
    vendors: useStorage('worker:vendors', []),
    entity: useStorage('worker:worker', {}),
  }),

  getters: {
    displayName(state) {
      let name = this.entity?.name?.split(' ')
      return name?.at(0) || 'Account'
    },

    email(state) {
      return this.entity?.email
    },

    hasRole(state) {
      return (role, key) =>
        this.isAdmin ||
        state.entity?.roles?.findIndex(
          (r) => r.role == role && (r.vendor == key || r.event == key)
        ) >= 0
    },

    isAdmin(state) {
      return state.entity?.isAdmin
    },

    key(state) {
      return state.entity?.key
    },

    prefs(state) {
      return state.entity?.prefs || {}
    },
  },

  actions: {
    /**
     * Check if user is authenticated via backend session cookie.
     * Caches the result to avoid redundant API calls.
     * @returns {Promise<boolean>} true if authenticated
     */
    async isAuthenticated() {
      // Return cached result if available
      if (this._authChecked !== undefined) {
        return this._authChecked
      }

      try {
        const response = await liffapi.getCurrentWorker('user')
        this._authChecked = true
        // Optionally populate minimal user data
        if (response.worker && !this.entity?.key) {
          this.entity = response.worker
        }
        return true
      } catch (e) {
        this._authChecked = false
        return false
      }
    },

    /**
     * Full login - fetches worker with vendors.
     * Called by app components after router auth check passes.
     */
    async login() {
      let vendorStore = useVendorStore()

      return liffapi
        .getCurrentWorker('full')
        .then((response) => {
          this._authChecked = true  // Mark as authenticated
          this.setWorker(response.worker)
          this.vendors = response.vendors
          if (!vendorStore?.key && response.vendors?.length > 0) {
            vendorStore.setVendor(response.vendors[0])
          }
          return response
        })
      // Note: Auth errors (401/403) are handled by axios interceptor in liffapi.js
      // Do not add a catch block here that redirects - it will conflict with centralized error handling
    },


    async logout() {
      await localStorage.clear()
      this.vendors = []
      this.entity = {}
    },

    async setPref(prop, value) {
      return liffapi.workers.updatePrefs(this.entity.key, { [prop]: value }).then((response) => {
        Object.assign(this.entity.prefs, response.entity)
        return response
      })
    },

    setWorker(user) {
      if (user?.key && user.key != this.entity?.key) {
        // clear user specific settings
        this.vendors = []
      }

      this.entity = user
    },
  },
})
